// Composer "실제 데이터 반영" 모달 — 조직/현장/점검(회차)/보고서(선택)/계정을 세로로 골라
// 확정 시에만 커밋.
//
// 인라인 컨텍스트 바를 대체한다. 모달 안 선택은 draft 로컬 state로만 편집하고(즉시 미리보기에
// 반영하지 않음), [반영] 클릭 시에만 onConfirm으로 상위 previewContext에 커밋한다. draft 초기값은
// 현재 previewContext(재오픈 시 이전 선택 유지). 오버레이·z-index(z-[70])·Esc/오버레이 닫기는
// 공용 Modal이 소유한다.
//
// ## 성격 분리(결정 2026-09-04) — 모달=실데이터, 속성 패널=모사
//
// storable 축의 즉석 모사 UI(「＋ 모사 값 추가」·「현재 값을 프리셋으로 저장」·프리셋 적용)는
// 여기서 뺐다 — 모사 저작·적용은 전역 속성 패널 「모사 데이터」 구획(SimulationPresetsSection)
// 몫이다. 단 **plan 축(분할 그룹)만은 이 모달에 남는다**: 그 값은 점검·시점 종속 계획 산출물이라
// 프리셋에 담기지 않고("적용 시점에 고릅니다" — 프리셋 규약), 고르는 유일한 통로가 여기다.
// 그 유일한 잔존 축은 「데이터 모사」 구획이 아니라 **캐스케이드의 한 단계**(보고서(선택) 하위 ·
// 계정 위 칸 — §3 개정 순서: 점검 → 보고서 → 분할 그룹 → 계정, 위=데이터 원천·아래=형상 조정)로
// 뜬다(planAxisAsField — §1 개정 절의 승격). 보고서 연결과 **조합 가능**하다(§3 개정 — 본문 행은
// 연결 중에도 컨텍스트 resolve라 직교). 모사 구획 머리글·「＋ 모사 값 추가」는 이 모달에 더 이상 없다.

import { useMemo, useState } from 'react';
import { Button, Modal } from '@safetyhub/ui';
import type {
  PreviewSimulationAxis,
  PreviewSimulationOption,
  ReportComposerPreviewContext,
} from '@safetyhub/shared';
import type { EditorPreviewContext } from './previewLinkedReport';
import { isPresetStorableAxis } from './simulationPresets';
import { PreviewContextBar, type PreviewContextLabels } from './PreviewContextBar';

interface Props {
  /** 모달 초기 draft — 현재 적용/선택된 컨텍스트(재오픈 시 이전 선택 유지). */
  initialContext: EditorPreviewContext;
  /** 초기 라벨 — 옵션 fetch 완료 전 바로 [반영]해도 요약 이름이 유지되게. */
  initialLabels: PreviewContextLabels;
  /** [반영] — draft 컨텍스트 + 라벨을 확정. */
  onConfirm: (ctx: EditorPreviewContext, labels: PreviewContextLabels) => void;
  /** 취소/오버레이/Esc. */
  onCancel: () => void;
  /** 모사 축 후보(선언 파생 — 편집 중 템플릿 기준). PreviewContextBar JSDoc 참조. */
  simulationAxes?: PreviewSimulationAxis[];
  /** `plan` 축 선택지 조회 — ⚠️ 부수효과 있음. 저작자가 그 축을 추가하는 조작에서만 호출된다. */
  onLoadAxisOptions?: (
    axis: PreviewSimulationAxis,
    ctx: ReportComposerPreviewContext,
  ) => Promise<PreviewSimulationOption[]>;
}

export function RealDataModal({
  initialContext,
  initialLabels,
  onConfirm,
  onCancel,
  simulationAxes,
  onLoadAxisOptions,
}: Props) {
  const [draft, setDraft] = useState<EditorPreviewContext>(initialContext);
  const [draftLabels, setDraftLabels] = useState<PreviewContextLabels>(initialLabels);

  // 모달의 모사 축 = plan 축(분할 그룹)만(파일 헤더 §성격 분리). 필터 결과를 memo하는 이유:
  // draft 타이핑마다 새 배열이 내려가면 바(bar)의 축 파생 memo가 전부 재계산된다(identity 의존).
  const planOnlyAxes = useMemo(
    () => simulationAxes?.filter((axis) => !isPresetStorableAxis(axis)),
    [simulationAxes],
  );

  // draft에 엔티티가 하나라도 있으면 반영 가능.
  const canConfirm =
    !!draft.companyId ||
    !!draft.siteId ||
    !!draft.inspectionId ||
    !!draft.inspectionIds?.length ||
    !!draft.userId;

  // Enter는 콤보 검색과 충돌하므로 default 액션을 두지 않는다(base Modal도 Enter를 강제하지 않음).
  return (
    <Modal
      open
      onClose={onCancel}
      title="실제 데이터 반영"
      size="md"
      // ⛔ 예외(이유② — 포탈 안 된 떠 있는 UI): 본문의 `PreviewContextBar`가 팝업을
      // `absolute z-30 … max-h-60`으로 그리고(두 자리 중 하나는 role=listbox) `CatalogCombobox`도
      // 같은 형태인데 **둘 다 포탈을 쓰지 않는다**. base가 본문에 `overflow-y-auto`를 걸면 그
      // 팝업들이 본문 경계에서 **새로 잘린다**(tsc·spec·jsdom 어느 것도 못 보는 축이다).
      bodyScroll={false}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            취소
          </Button>
          <Button variant="primary" onClick={() => onConfirm(draft, draftLabels)} disabled={!canConfirm}>
            반영
          </Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-gray-500">
        조직 · 현장 · 점검(회차) · 계정을 선택하면 그 실제 데이터로 미리보기를 렌더합니다.
        점검에 연결된 보고서를 고르면 그 보고서의 저장 데이터로 렌더합니다.
      </p>
      <PreviewContextBar
        value={draft}
        onChange={setDraft}
        onLabelsResolved={setDraftLabels}
        vertical
        showReportPicker
        planAxisAsField
        simulationAxes={planOnlyAxes}
        onLoadAxisOptions={onLoadAxisOptions}
        initialSimulationLabels={initialLabels.simulation}
        initialReportLabel={initialLabels.report}
      />
    </Modal>
  );
}
